package dev.lingvo.site

import com.varabyte.kobweb.core.init.InitKobweb
import com.varabyte.kobweb.core.init.InitKobwebContext
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.w3c.dom.Element

/** Routing initializes before @App; page context is unavailable in the application wrapper. */
@InitKobweb
fun initPublicMetadata(context: InitKobwebContext) {
    fun update(path: String) {
        document.head?.let { applyPublicMetadata(path, it) }
    }
    update(window.location.pathname)
    context.router.addRouteInterceptor { update(path) }
    // Browser history traversal does not pass through the router's navigation interceptors.
    window.addEventListener("popstate", { update(window.location.pathname) })
}

private data class PublicPageMetadata(val title: String, val description: String)

private val publicPages = mapOf(
    "/" to PublicPageMetadata("Lingvo — Localization for software teams", "Manage software translations with shared review, structural QA, a CLI and agent tools. Keep localization connected to your repository."),
    "/pricing" to PublicPageMetadata("Pricing — Lingvo", "Compare Lingvo plans, workspace limits and translation credits. Manual translation, import, export and QA remain available without machine translation."),
    "/agents" to PublicPageMetadata("Localization with agents — Lingvo", "Connect your coding agent to Lingvo for shared localization context, scoped tools, review and QA across releases."),
    "/docs" to PublicPageMetadata("Documentation — Lingvo", "Set up Lingvo, import your first strings and connect the CLI or an MCP client. Learn translation review, QA and repository workflows."),
    "/docs/cli" to PublicPageMetadata("CLI guide — Lingvo", "Install the Lingvo CLI, configure localization files, push and pull translations, run QA and add a CI gate."),
    "/docs/cli/commands" to PublicPageMetadata("CLI command reference — Lingvo", "Explore Lingvo CLI commands, options, scopes and output contracts for localization automation."),
    "/docs/mcp" to PublicPageMetadata("MCP guide — Lingvo", "Connect an MCP client to Lingvo with scoped credentials. Understand hosted and local agent tools, review boundaries and setup."),
    "/store-audit" to PublicPageMetadata("Free App Store localization check — Lingvo", "Paste an App Store link and see which storefronts still show your app's English listing. Runs in your browser; nothing is sent to Lingvo."),
    "/docs/stores" to PublicPageMetadata("Store listings guide — Lingvo", "Translate your App Store and Google Play listing with your glossary, check it against each store's limits, review a per-language diff and push it from the web app."),
    "/docs/screenshots" to PublicPageMetadata("Store screenshots guide — Lingvo", "Plan App Store and Google Play screenshots, capture localized app screens, review captions and images, and prepare approved sets for export or store delivery."),
    "/docs/formats" to PublicPageMetadata("Localization file formats — Lingvo", "Review supported localization formats, placeholder and plural handling, and import/export limitations."),
    "/privacy" to PublicPageMetadata("Privacy — Lingvo", "Understand what Lingvo stores, operational records, subprocessors and how workspace data is handled."),
    "/terms" to PublicPageMetadata("Terms — Lingvo", "Read the terms for using Lingvo's localization service."),
    "/refunds" to PublicPageMetadata("Refund policy — Lingvo", "Read Lingvo's refund policy and how to request help with billing."),
)

/** Only allowlisted public routes may contribute URLs or descriptions to the document head. */
internal fun applyPublicMetadata(path: String, head: Element) {
    // The template fallback shares the semantic description slot with route metadata.
    val previous = head.querySelectorAll("[data-lingvo-metadata], meta[name=description]")
    for (index in 0 until previous.length) previous.item(index)?.let { head.removeChild(it) }
    val document = head.ownerDocument ?: return
    fun meta(attribute: String, name: String, content: String) {
        val element = document.createElement("meta")
        element.setAttribute("data-lingvo-metadata", "true")
        element.setAttribute(attribute, name)
        element.setAttribute("content", content)
        head.appendChild(element)
    }
    val route = path.substringBefore('?').substringBefore('#').trimEnd('/').ifEmpty { "/" }
    val page = publicPages[route]
    meta("name", "robots", if (page == null) "noindex, nofollow" else "index, follow")
    if (page == null) return
    val url = "https://lingvo.dev$route"
    val canonical = document.createElement("link")
    canonical.setAttribute("data-lingvo-metadata", "true")
    canonical.setAttribute("rel", "canonical")
    canonical.setAttribute("href", url)
    head.appendChild(canonical)
    meta("name", "description", page.description)
    meta("property", "og:type", "website")
    meta("property", "og:site_name", "Lingvo")
    meta("property", "og:title", page.title)
    meta("property", "og:description", page.description)
    meta("property", "og:url", url)
    val imageUrl = "https://lingvo.dev/social/lingvo.png"
    val imageAlt = "Lingvo — Localization that stays with your code. Shared context, translation review, structural QA, CLI and agent tools."
    meta("property", "og:image", imageUrl)
    meta("property", "og:image:type", "image/png")
    meta("property", "og:image:width", "1200")
    meta("property", "og:image:height", "630")
    meta("property", "og:image:alt", imageAlt)
    meta("name", "twitter:card", "summary_large_image")
    meta("name", "twitter:image", imageUrl)
    meta("name", "twitter:image:alt", imageAlt)
    meta("name", "twitter:title", page.title)
    meta("name", "twitter:description", page.description)

    val structured = document.createElement("script")
    structured.setAttribute("data-lingvo-metadata", "true")
    structured.setAttribute("type", "application/ld+json")
    structured.textContent = buildJsonObject {
        put("@context", "https://schema.org")
        put("@graph", buildJsonArray {
            if (route == "/") add(buildJsonObject {
                put("@type", "WebSite")
                put("@id", "https://lingvo.dev/#website")
                put("name", "Lingvo")
                put("url", "https://lingvo.dev/")
            })
            add(buildJsonObject {
                put("@type", "WebPage")
                put("@id", "$url#webpage")
                put("url", url)
                put("name", page.title)
                put("description", page.description)
                put("isPartOf", buildJsonObject { put("@id", "https://lingvo.dev/#website") })
            })
        })
    }.toString()
    head.appendChild(structured)
}
